import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiCheckCircle, FiClock, FiTrash2 } from 'react-icons/fi';
import { BsPinAngle } from 'react-icons/bs';
import { toast } from 'sonner';
import { gnomes } from '../../constants/gnomes';

const GNOME_ID_MAP = {
  1: 'gnomepoleon',
  2: 'jeeves',
  3: 'gnorm',
  4: 'stampson',
  5: 'benjamin',
  6: 'kestrel',
  7: 'doug'
};

const PinnedTasksModal = ({ isOpen, onClose, workspaceId, onTasksCountChange }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (workspaceId) {
            fetchTasks();
        }
    }, [workspaceId, isOpen]); // Refetch when opened to get latest data

    useEffect(() => {
        if (onTasksCountChange) {
            onTasksCountChange(tasks.filter(t => t.status === 'pending').length);
        }
    }, [tasks, onTasksCountChange]);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const [pinsRes, gnomesRes] = await Promise.all([
                supabase.from('user_pins').select('*').eq('workspace_id', workspaceId),
                supabase.from('gnome_tasks').select('*').eq('workspace_id', workspaceId)
            ]);

            if (pinsRes.error) throw pinsRes.error;
            if (gnomesRes.error) throw gnomesRes.error;

            const mappedPins = (pinsRes.data || []).map(p => ({
                ...p,
                type: 'pin'
            }));

            const mappedGnomeTasks = (gnomesRes.data || []).map(gt => ({
                ...gt,
                type: 'gnome',
                content: gt.task_description || gt.title || gt.description || gt.content || 'Task',
                status: (gt.status === 'completed' || gt.status === 'failed') ? 'completed' : 'pending',
                created_at: gt.created_at || gt.due_at || new Date().toISOString()
            }));

            const combined = [...mappedPins, ...mappedGnomeTasks].sort((a, b) => {
                if (a.status === b.status) {
                    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                    return dateB - dateA;
                }
                return a.status === 'pending' ? -1 : 1;
            });

            setTasks(combined);
        } catch (err) {
            // console.error("Error fetching pinned tasks:", err);
            toast.error("Failed to load pinned tasks.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (task) => {
        const newStatus = task.status === 'pending' ? 'completed' : 'pending';
        try {
            if (task.type === 'pin') {
                const { error } = await supabase
                    .from('user_pins')
                    .update({ status: newStatus })
                    .eq('id', task.id);
                if (error) throw error;
            } else if (task.type === 'gnome') {
                const dbStatus = newStatus === 'completed' ? 'completed' : 'pending';
                const { error } = await supabase
                    .from('gnome_tasks')
                    .update({ status: dbStatus })
                    .eq('id', task.id);
                if (error) throw error;
            }

            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t).sort((a, b) => {
                if (a.status === b.status) {
                    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                    return dateB - dateA;
                }
                return a.status === 'pending' ? -1 : 1;
            }));

            if (newStatus === 'completed') toast.success("Task completed!");
        } catch (err) {
            // console.error("Error updating task status:", err);
            toast.error("Failed to update task.");
        }
    };

    const handleDelete = async (task) => {
        try {
            if (task.type === 'pin') {
                const { error } = await supabase.from('user_pins').delete().eq('id', task.id);
                if (error) throw error;
            } else if (task.type === 'gnome') {
                const { error } = await supabase.from('gnome_tasks').delete().eq('id', task.id);
                if (error) throw error;
            }

            setTasks(prev => prev.filter(t => t.id !== task.id));
            toast.success("Task deleted.");
        } catch (err) {
            // console.error("Error deleting task:", err);
            toast.error("Failed to delete task.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-charcoal-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]">
                <div className="shrink-0 flex items-center justify-between p-4 border-b border-charcoal-200 dark:border-charcoal-800 bg-charcoal-50 dark:bg-charcoal-800/50">
                    <div className="flex items-center space-x-2">
                        <SafeIcon icon={BsPinAngle} className="text-xl text-blue-600" />
                        <h2 className="text-lg font-bold text-charcoal-900 dark:text-white">Pinned Tasks & Notes</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-charcoal-500 hover:text-charcoal-900 dark:hover:text-white hover:bg-charcoal-200 dark:hover:bg-charcoal-700 rounded-full transition-colors"
                    >
                        <SafeIcon icon={FiX} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                    {loading ? (
                        <div className="flex justify-center items-center h-40 text-charcoal-500">
                            Loading tasks...
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-charcoal-400">
                            <SafeIcon icon={BsPinAngle} className="text-4xl mb-2 opacity-50" />
                            <p>No pinned tasks yet. Pin messages from Gnome Chat!</p>
                        </div>
                    ) : (
                        tasks.map((task) => (
                            <div
                                key={`${task.type}-${task.id}`}
                                className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                                    task.status === 'completed'
                                        ? 'bg-charcoal-50 dark:bg-charcoal-800/50 border-charcoal-200 dark:border-charcoal-700 opacity-60'
                                        : 'bg-white dark:bg-charcoal-800 border-blue-200 dark:border-blue-900/30 shadow-sm'
                                }`}
                            >
                                <button
                                    onClick={() => handleToggleStatus(task)}
                                    className={`mt-1 flex-shrink-0 transition-colors ${
                                        task.status === 'completed'
                                            ? 'text-green-500'
                                            : 'text-charcoal-300 hover:text-green-500'
                                    }`}
                                >
                                    <SafeIcon icon={FiCheckCircle} className="text-xl" />
                                </button>

                                {task.type === 'gnome' && task.gnome_id && (() => {
                                    const gnomeSlug = GNOME_ID_MAP[task.gnome_id];
                                    const gnome = gnomes.find(g => g.slug === gnomeSlug) || gnomes[0];
                                    return (
                                        <div className={`w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-charcoal-200 dark:border-charcoal-700 ${gnome.bgColorClass || 'bg-charcoal-100 dark:bg-charcoal-800'}`}>
                                            {gnome.image || gnome.headshot ? (
                                                <img src={gnome.image || gnome.headshot} alt={gnome.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-white">
                                                    <SafeIcon icon={gnome.icon} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm whitespace-pre-wrap leading-relaxed ${task.status === 'completed' ? 'text-charcoal-500 line-through' : 'text-charcoal-900 dark:text-charcoal-100'}`}>
                                        {task.content}
                                    </div>
                                    <div className="flex items-center gap-2 mt-2 text-xs text-charcoal-400">
                                        <SafeIcon icon={FiClock} />
                                        <span>{task.created_at ? new Date(task.created_at).toLocaleString() : 'No date'}</span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleDelete(task)}
                                    className="p-1.5 text-charcoal-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                    title="Delete Note"
                                >
                                    <SafeIcon icon={FiTrash2} />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <div className="shrink-0 p-4 border-t border-charcoal-200 dark:border-charcoal-800 bg-charcoal-50 dark:bg-charcoal-800/50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-charcoal-900 text-white rounded-lg hover:bg-charcoal-800 transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PinnedTasksModal;
